import { useMemo, useRef } from 'react'

import { Button } from '@/components/ui/button.tsx'
import { cn } from '@/lib/utils.ts'

import { RejectionModal } from '@/components/form/status/RejectionModal.tsx'
import { STORY_STATUSES } from '@/constants/STORY_STATUSES.ts'
import { STORY_TYPES } from '@/constants/STORY_TYPES.ts'
import { useMutateStoryStatus } from '@/requests/mutations/useMutateStoryStatus.ts'
import { isStartableStory } from '@/utils/story.ts'

type Props = {
  storyId: string
  status: StoryStatus
  estimate: StoryPoints
  storyType: StoryType
}

export const StatusButton = ({ storyId, status, estimate, storyType }: Props) => {
  const mutate = useMutateStoryStatus(storyId)
  const rejectionModalRef = useRef<Modal>(null)

  const button = useMemo(() => {
    switch (status) {
      case STORY_STATUSES.UNSCHEDULED:
      case STORY_STATUSES.UNSTARTED:
        if (!isStartableStory(storyId, estimate, storyType)) {
          return null
        }

        if (storyType === STORY_TYPES.RELEASE) {
          return <StatusButtonTemplate statusAction="Finish" onClick={() => mutate(STORY_STATUSES.ACCEPTED)} />
        }

        return <StatusButtonTemplate statusAction="Start" onClick={() => mutate(STORY_STATUSES.STARTED)} />

      case STORY_STATUSES.STARTED:
        return (
          <StatusButtonTemplate
            statusAction="Finish"
            onClick={() => {
              if (storyType === STORY_TYPES.CHORE) {
                mutate(STORY_STATUSES.ACCEPTED)
              } else {
                mutate(STORY_STATUSES.FINISHED)
              }
            }}
          />
        )

      case STORY_STATUSES.FINISHED:
        return <StatusButtonTemplate statusAction="Deliver" onClick={() => mutate(STORY_STATUSES.DELIVERED)} />

      case STORY_STATUSES.DELIVERED:
        return (
          <div className="flex items-center gap-1">
            <StatusButtonTemplate statusAction="Accept" onClick={() => mutate(STORY_STATUSES.ACCEPTED)} />
            <StatusButtonTemplate statusAction="Reject" onClick={() => rejectionModalRef.current?.showModal()} />
          </div>
        )

      case STORY_STATUSES.REJECTED:
        return <StatusButtonTemplate statusAction="Restart" onClick={() => mutate(STORY_STATUSES.STARTED)} />

      default:
        return null
    }
  }, [storyId, status, mutate, estimate, storyType])

  return (
    <>
      {button}
      <RejectionModal ref={rejectionModalRef} storyId={storyId} />
    </>
  )
}

type StatusButtonProps = {
  statusAction: string
  onClick: () => void
}

const StatusButtonTemplate = ({ statusAction, onClick }: StatusButtonProps) => {
  const getButtonStyle = (statusAction: string) => {
    switch (statusAction) {
      case 'Start':
        return 'text-contents-primary bg-taskProgressButton-start hover:after:bg-stateLayer-onLight-hovered hover:after:opacity-8'
      case 'Finish':
        return 'text-contents-onDarkPrimary bg-taskProgressButton-finish'
      case 'Deliver':
        return 'text-contents-onDarkPrimary bg-taskProgressButton-deliver'
      case 'Accept':
        return 'text-contents-onDarkPrimary bg-taskProgressButton-accept'
      case 'Reject':
        return 'text-contents-onDarkPrimary bg-taskProgressButton-reject'
      case 'Restart':
        return 'text-contents-alert bg-taskProgressButton-restart'
    }
  }

  return (
    <Button
      variant="primary"
      type="button"
      className={cn('rounded-xs py-1 px-1.5 font-EN-KR-tag-bold w-12 h-5', getButtonStyle(statusAction))}
      onClick={(e) => {
        e.stopPropagation()
        onClick()
      }}
    >
      {statusAction}
    </Button>
  )
}
